import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { Form, PrimaryButton, SecondaryButton, FieldTextInput } from '../../components';
import * as validators from '../../util/validators';
import { sendOtpCode, verifyOtpCode, loginWithOtp, signupWithOtp } from '../../ducks/Auth.duck';
import { uiElementClicked } from '../../ducks/UI.duck';
import {
  OTP_STARTED,
  OTP_RESEND_CLICKED,
  OTP_BACK_TO_PASSWORD,
  OTP_WRONG_EMAIL,
} from '../../tracking/types';

import css from './OtpForm.module.css';

const RESEND_COOLDOWN_MS = 60 * 1000;
const RESEND_GRACE_SENDS = 2;

const isRateLimitError = error => {
  if (!error) return false;
  if (error.status === 429) return true;
  const msg = `${error.error || ''} ${error.message || ''}`.toLowerCase();
  return msg.includes('too many') || msg.includes('please wait');
};

// Same buckets and wording as Buck (buck-chat src/utils/authApi.ts and
// SignupDrawer/hooks/useCodeStep.ts). Messages never say whether an account
// exists or why a login failed; the reason stays in tracking only.
const errorText = error =>
  `${(error && error.error) || ''} ${(error && error.message) || ''}`.toLowerCase();

export const verifyErrorMessageId = error => {
  const text = errorText(error);
  const status = error && (error.status || error.statusCode);
  if (
    text.includes('max attempts') ||
    text.includes('too many attempts') ||
    text.includes('maximum')
  ) {
    return 'OtpForm.verifyCodeTooManyAttempts';
  }
  if (text.includes('expired')) {
    return 'OtpForm.verifyCodeExpired';
  }
  if (text.includes('invalid') || text.includes('incorrect') || status === 400) {
    return 'OtpForm.verifyCodeInvalid';
  }
  return 'OtpForm.verifyCodeFailed';
};

export const OtpFormComponent = props => {
  // 'start' shows only the secondary entry button on the login page.
  // A deep link or refresh on ?otp=email|code|profile opens the email step
  // straight away (the email, code and token are not kept across reloads), so
  // the first onStepChange never reports 'start' and strips ?otp from the URL.
  const [step, setStep] = useState(() =>
    ['email', 'code', 'profile'].includes(props.requestedStep) ? 'email' : 'start'
  ); // 'start' | 'email' | 'code' | 'profile'
  const [email, setEmail] = useState('');
  const [idpToken, setIdpToken] = useState(null);
  const [returnedContext, setReturnedContext] = useState(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const resendSendCountRef = useRef(0);
  const [resendCooldownEndsAt, setResendCooldownEndsAt] = useState(null);
  const [resendTimeRemaining, setResendTimeRemaining] = useState(null);
  const [errorMessageId, setErrorMessageId] = useState(null);
  const { onStepChange, requestedStep, onNavigate, onNavigateBack } = props;

  // The URL (?otp=email|code) can ask for a step, e.g. after browser Back or a
  // deep link. The code step needs an email, so without one it falls back to
  // the email step. The profile step is never driven by the URL.
  useEffect(() => {
    if (requestedStep === undefined || step === 'profile') {
      return;
    }
    let target = 'start';
    if (requestedStep === 'email') target = 'email';
    if (requestedStep === 'code') target = email ? 'code' : 'email';
    if (target !== step) {
      setErrorMessageId(null);
      setStep(target);
    }
  }, [requestedStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Forward moves go through the parent (which adds a history entry) when it
  // handles navigation; otherwise the step is local only.
  const goTo = target => (onNavigate ? onNavigate(target) : setStep(target));
  const goBack = fallback => (onNavigateBack ? onNavigateBack(fallback) : setStep(fallback));

  // Let the parent page know which step is shown, so it can hide the rest of
  // the login page while the user enters the code / completes the profile.
  useEffect(() => {
    if (onStepChange) {
      onStepChange(step);
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    rootClassName,
    className,
    onSuccess,
    context = null,
    sendOtpCodeInProgress,
    verifyOtpCodeInProgress,
    signupInProgress,
    onSubmitSendCode,
    onSubmitVerifyCode,
    onSubmitLogin,
    onSubmitSignup,
    onTrackClick,
    intl,
  } = props;

  const trackClick = event => {
    if (onTrackClick) {
      onTrackClick(event);
    }
  };

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldownEndsAt) {
      const interval = setInterval(() => {
        const remaining = Math.max(0, resendCooldownEndsAt - Date.now());
        setResendTimeRemaining(remaining);

        if (remaining === 0) {
          clearInterval(interval);
          setResendCooldownEndsAt(null);
          setResendTimeRemaining(null);
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
    return undefined;
  }, [resendCooldownEndsAt]);

  const formatTimeRemaining = ms => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const sendErrorMessageId = error =>
    isRateLimitError(error) ? 'OtpForm.sendCodeRateLimited' : 'OtpForm.sendCodeFailed';

  const clearError = () => {
    if (errorMessageId) {
      setErrorMessageId(null);
    }
  };

  const handleSendCode = async values => {
    setErrorMessageId(null);
    try {
      await onSubmitSendCode(values.email, context);
    } catch (error) {
      setEmail(values.email);
      setErrorMessageId(sendErrorMessageId(error));
      return;
    }
    setEmail(values.email);
    goTo('code');
    resendSendCountRef.current = 1;
    setResendCooldownEndsAt(null);
    setResendTimeRemaining(null);
  };

  const handleVerifyCode = async values => {
    setErrorMessageId(null);
    let result;
    try {
      // Returns idpToken directly (5 min expiry starts NOW)
      result = await onSubmitVerifyCode(email, values.code);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('handleVerifyCode verify error', error);
      setErrorMessageId(verifyErrorMessageId(error));
      return;
    }
    setIdpToken(result.idpToken);
    setReturnedContext(result.context);
    setTokenExpiresAt(Date.now() + 5 * 60 * 1000); // 5 minutes from now

    // The code was right; a failure from here on is a login problem, so it
    // must not say the code was wrong. The page's own login error is hidden
    // while the OTP step is shown, so the message is shown here.
    let loginResult;
    try {
      // Server returns { signupRequired: true } if the user doesn't exist
      loginResult = await onSubmitLogin(result.idpToken);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('handleVerifyCode login error', error);
      // Like Buck: one generic message, no account or verification details.
      setErrorMessageId('OtpForm.verifyCodeFailed');
      return;
    }

    if (loginResult?.signupRequired) {
      // New user - need to collect profile info
      setStep('profile'); // Collect name for new users - must complete within 5 min!
    } else if (onSuccess) {
      // Existing user - login was successful
      onSuccess(result.context);
    }
  };

  const handleCompleteSignup = async values => {
    // User must complete this within 5 minutes of code verification
    if (tokenExpiresAt && Date.now() > tokenExpiresAt) {
      throw new Error('Session expired. Please request a new code.');
    }

    await onSubmitSignup(idpToken, {
      email,
      firstName: values.firstName,
      lastName: values.lastName,
    });
    if (onSuccess) {
      onSuccess(returnedContext);
    }
  };

  const handleResendCode = async () => {
    trackClick(OTP_RESEND_CLICKED);
    setErrorMessageId(null);
    try {
      await onSubmitSendCode(email, context);
    } catch (error) {
      setErrorMessageId(
        isRateLimitError(error) ? 'OtpForm.sendCodeRateLimited' : 'OtpForm.sendCodeFailed'
      );
      return;
    }
    resendSendCountRef.current += 1;
    if (resendSendCountRef.current >= RESEND_GRACE_SENDS) {
      const cooldownEndsAt = Date.now() + RESEND_COOLDOWN_MS;
      setResendCooldownEndsAt(cooldownEndsAt);
      setResendTimeRemaining(RESEND_COOLDOWN_MS);
    }
  };

  const handleStart = () => {
    trackClick(OTP_STARTED);
    setErrorMessageId(null);
    goTo('email');
  };

  const handleUsePassword = () => {
    trackClick(OTP_BACK_TO_PASSWORD);
    setErrorMessageId(null);
    goBack('start');
  };

  const handleChangeEmail = () => {
    setErrorMessageId(null);
    goBack('email');
  };

  // Back on the email step (button or browser) resets the resend limits.
  useEffect(() => {
    if (step === 'email') {
      resendSendCountRef.current = 0;
      setResendCooldownEndsAt(null);
      setResendTimeRemaining(null);
    }
  }, [step]);

  const errorBlock = errorMessageId ? (
    <div className={css.errorMessage} role="alert" data-testid="otp-error">
      <FormattedMessage id={errorMessageId} />
    </div>
  ) : null;

  const classes = classNames(rootClassName || css.root, className);

  // Entry point on the login page: one secondary button that opens the email step.
  if (step === 'start') {
    return (
      <div className={classes}>
        <SecondaryButton
          data-testid="otp-start"
          type="button"
          className={css.startButton}
          onClick={handleStart}
        >
          <FormattedMessage id="OtpForm.start" />
        </SecondaryButton>
        <p className={css.startHelper}>
          <FormattedMessage id="OtpForm.startHelper" />
        </p>
      </div>
    );
  }

  // Email step
  if (step === 'email') {
    const emailLabel = intl.formatMessage({
      id: 'OtpForm.emailLabel',
    });
    const emailPlaceholder = intl.formatMessage({
      id: 'OtpForm.emailPlaceholder',
    });
    const emailRequiredMessage = intl.formatMessage({
      id: 'OtpForm.emailRequired',
    });
    const emailRequired = validators.required(emailRequiredMessage);
    const emailInvalidMessage = intl.formatMessage({
      id: 'OtpForm.emailInvalid',
    });
    const emailValid = validators.emailFormatValid(emailInvalidMessage);

    return (
      <FinalForm
        onSubmit={handleSendCode}
        initialValues={{ email }}
        render={({ handleSubmit, invalid }) => (
          <Form className={classes} onSubmit={handleSubmit} onChange={clearError}>
            <h2 className={css.stepHeading}>
              <FormattedMessage id="OtpForm.emailHeading" />
            </h2>
            <p className={css.stepIntro}>
              <FormattedMessage id="OtpForm.emailIntro" />
            </p>
            <FieldTextInput
              data-testid="otp-form-email"
              className={css.email}
              type="email"
              id="otpEmail"
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />
            {errorBlock}
            <div className={css.bottomWrapper}>
              <PrimaryButton
                data-testid="otp-send-code"
                type="submit"
                inProgress={sendOtpCodeInProgress}
                disabled={invalid || sendOtpCodeInProgress}
                isFullWidth
              >
                <FormattedMessage id="OtpForm.sendCode" />
              </PrimaryButton>
              <SecondaryButton
                data-testid="otp-back-to-login"
                type="button"
                className={css.secondaryAction}
                onClick={handleUsePassword}
              >
                <FormattedMessage id="OtpForm.back" />
              </SecondaryButton>
            </div>
          </Form>
        )}
      />
    );
  }

  // Code verification step
  if (step === 'code') {
    const codeLabel = intl.formatMessage({
      id: 'OtpForm.codeLabel',
    });
    const codePlaceholder = intl.formatMessage({
      id: 'OtpForm.codePlaceholder',
    });
    const codeRequiredMessage = intl.formatMessage({
      id: 'OtpForm.codeRequired',
    });
    const codeRequired = validators.required(codeRequiredMessage);
    const codeInvalidMessage = intl.formatMessage({
      id: 'OtpForm.codeInvalid',
    });
    const codeValid = value => {
      // Normalize: remove dashes and convert to uppercase for validation
      const normalized = value ? value.replace(/-/g, '').toUpperCase() : '';
      // Only require exactly 8 characters - server will validate the actual charset
      return normalized.length === 8 ? undefined : codeInvalidMessage;
    };

    // Handle paste: strip dashes and normalize
    const handleCodePaste = (e, form) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      // Remove dashes and convert to uppercase
      const normalized = pastedText.replace(/-/g, '').toUpperCase();
      form.change('code', normalized);
    };

    // Handle change: normalize input (strip dashes, uppercase)
    const handleCodeChange = (e, form) => {
      const { value } = e.target;
      // Remove dashes and convert to uppercase
      const normalized = value.replace(/-/g, '').toUpperCase();
      form.change('code', normalized);
      clearError();
    };

    return (
      <FinalForm
        onSubmit={handleVerifyCode}
        render={({ handleSubmit, invalid, form }) => (
          <Form className={classes} onSubmit={handleSubmit}>
            <h2 className={css.stepHeading}>
              <FormattedMessage id="OtpForm.codeHeading" />
            </h2>
            <p className={css.emailDisplay}>
              <FormattedMessage
                id="OtpForm.codeSentTo"
                values={{ email: <strong>{email}</strong> }}
              />{' '}
              <button
                type="button"
                data-testid="otp-wrong-email"
                className={css.inlineLink}
                onClick={() => {
                  trackClick(OTP_WRONG_EMAIL);
                  handleChangeEmail();
                }}
              >
                <FormattedMessage id="OtpForm.wrongEmail" />
              </button>
            </p>
            <FieldTextInput
              data-testid="otp-form-code"
              className={errorBlock ? css.fieldWithError : css.code}
              type="text"
              id="otpCode"
              name="code"
              autoComplete="one-time-code"
              maxLength={9}
              label={codeLabel}
              placeholder={codePlaceholder}
              validate={validators.composeValidators(codeRequired, codeValid)}
              onPaste={e => handleCodePaste(e, form)}
              onChange={e => handleCodeChange(e, form)}
            />
            {errorBlock}
            <div className={css.bottomWrapper}>
              <PrimaryButton
                data-testid="otp-verify-code"
                type="submit"
                inProgress={verifyOtpCodeInProgress}
                disabled={invalid || verifyOtpCodeInProgress}
                isFullWidth
              >
                <FormattedMessage id="OtpForm.verifyCode" />
              </PrimaryButton>
              <SecondaryButton
                data-testid="otp-back"
                type="button"
                className={css.secondaryAction}
                onClick={handleChangeEmail}
              >
                <FormattedMessage id="OtpForm.back" />
              </SecondaryButton>
              {(() => {
                const isResendCooldownActive =
                  resendTimeRemaining !== null && resendTimeRemaining > 0;
                const resendDisabled = sendOtpCodeInProgress || isResendCooldownActive;
                const resendLabel = isResendCooldownActive ? (
                  <FormattedMessage
                    id="OtpForm.resendCooldown"
                    values={{ time: formatTimeRemaining(resendTimeRemaining) }}
                  />
                ) : (
                  <FormattedMessage id="OtpForm.resendCode" />
                );
                return (
                  <button
                    type="button"
                    className={css.resendLink}
                    onClick={handleResendCode}
                    disabled={resendDisabled}
                  >
                    {resendLabel}
                  </button>
                );
              })()}
            </div>
          </Form>
        )}
      />
    );
  }

  // Profile step (new users only)
  if (step === 'profile') {
    const firstNameLabel = intl.formatMessage({
      id: 'OtpForm.firstNameLabel',
    });
    const firstNamePlaceholder = intl.formatMessage({
      id: 'OtpForm.firstNamePlaceholder',
    });
    const firstNameRequiredMessage = intl.formatMessage({
      id: 'OtpForm.firstNameRequired',
    });
    const firstNameRequired = validators.required(firstNameRequiredMessage);

    const lastNameLabel = intl.formatMessage({
      id: 'OtpForm.lastNameLabel',
    });
    const lastNamePlaceholder = intl.formatMessage({
      id: 'OtpForm.lastNamePlaceholder',
    });
    const lastNameRequiredMessage = intl.formatMessage({
      id: 'OtpForm.lastNameRequired',
    });
    const lastNameRequired = validators.required(lastNameRequiredMessage);

    const isExpired = tokenExpiresAt && Date.now() > tokenExpiresAt;
    const submitDisabled = isExpired || signupInProgress;

    return (
      <FinalForm
        onSubmit={handleCompleteSignup}
        render={({ handleSubmit, invalid }) => (
          <Form className={classes} onSubmit={handleSubmit}>
            {isExpired && (
              <div className={css.errorMessage}>
                <FormattedMessage id="OtpForm.sessionExpired" />
              </div>
            )}

            <FieldTextInput
              data-testid="otp-form-first-name"
              className={css.firstName}
              type="text"
              id="otpFirstName"
              name="firstName"
              autoComplete="given-name"
              label={firstNameLabel}
              placeholder={firstNamePlaceholder}
              validate={firstNameRequired}
            />
            <FieldTextInput
              data-testid="otp-form-last-name"
              className={css.lastName}
              type="text"
              id="otpLastName"
              name="lastName"
              autoComplete="family-name"
              label={lastNameLabel}
              placeholder={lastNamePlaceholder}
              validate={lastNameRequired}
            />
            <div className={css.bottomWrapper}>
              <PrimaryButton
                data-testid="otp-complete-signup"
                type="submit"
                inProgress={signupInProgress}
                disabled={invalid || submitDisabled}
                isFullWidth
              >
                <FormattedMessage id="OtpForm.completeSignup" />
              </PrimaryButton>
            </div>
          </Form>
        )}
      />
    );
  }

  return null;
};

OtpFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  context: null,
  onSuccess: null,
  onStepChange: null,
  onTrackClick: null,
  requestedStep: undefined,
  onNavigate: null,
  onNavigateBack: null,
};

const { string, func, bool, object } = PropTypes;

OtpFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  context: object,
  onSuccess: func,
  onStepChange: func,
  onTrackClick: func,
  requestedStep: string,
  onNavigate: func,
  onNavigateBack: func,
  sendOtpCodeInProgress: bool.isRequired,
  verifyOtpCodeInProgress: bool.isRequired,
  signupInProgress: bool.isRequired,
  onSubmitSendCode: func.isRequired,
  onSubmitVerifyCode: func.isRequired,
  onSubmitLogin: func.isRequired,
  onSubmitSignup: func.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { Auth } = state;
  return {
    sendOtpCodeInProgress: Auth.sendOtpCodeInProgress || false,
    verifyOtpCodeInProgress: Auth.verifyOtpCodeInProgress || false,
    loginInProgress: Auth.loginInProgress || false,
    signupInProgress: Auth.signupInProgress || false,
  };
};

const mapDispatchToProps = dispatch => ({
  onSubmitSendCode: (email, context) => dispatch(sendOtpCode(email, context)),
  onSubmitVerifyCode: (email, code) => dispatch(verifyOtpCode(email, code)),
  onSubmitLogin: idpToken => dispatch(loginWithOtp(idpToken)),
  onSubmitSignup: (idpToken, userData) => dispatch(signupWithOtp(idpToken, userData)),
  onTrackClick: event => dispatch(uiElementClicked({ event })),
});

const OtpForm = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(OtpFormComponent);

OtpForm.displayName = 'OtpForm';

export default OtpForm;
